import { useState } from "react";
import { Heart, Plus, Trash2, LogOut, Save } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import { Toaster as Sonner, toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyAEDItem = {
  unit_number: "",
  aed_serial_number: "",
  aed_sentinel_id: "",
  lot_number: "",
  expiration_date: "",
  install_date: "",
  consumable_make: "",
  model: "",
  case_or_cabinet: "",
  location_in_facility: "",
  is_911_district: false,
};

const CustomerPortal = () => {
  const [formData, setFormData] = useState({
    site_name: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    unit_count: 0,
  });

  const [aedItems, setAedItems] = useState([{ ...emptyAEDItem }]);
  const [saving, setSaving] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAEDChange = (index, field, value) => {
    setAedItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addAEDRow = () => {
    setAedItems((prev) => [...prev, { ...emptyAEDItem }]);
  };

  const removeAEDRow = (index) => {
    if (aedItems.length > 1) {
      setAedItems((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        unit_count: aedItems.length,
        aed_items: aedItems,
      };

      const response = await axios.post(`${API}/customer-forms`, payload);
      toast.success("Form saved successfully!", {
        description: `Form ID: ${response.data.id}`,
      });
    } catch (error) {
      console.error("Error saving form:", error);
      toast.error("Failed to save form", {
        description: error.response?.data?.detail || "Please try again",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleExit = () => {
    if (window.confirm("Are you sure you want to exit? Unsaved changes will be lost.")) {
      // Reset form
      setFormData({
        site_name: "",
        address: "",
        city: "",
        state: "",
        zip_code: "",
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        unit_count: 0,
      });
      setAedItems([{ ...emptyAEDItem }]);
      toast.info("Form cleared");
    }
  };

  return (
    <div className="min-h-screen bg-stark-bg p-4 md:p-8" data-testid="customer-portal">
      <Sonner 
        position="top-right" 
        theme="dark"
        toastOptions={{
          style: {
            background: '#0A0A0A',
            border: '1px solid #333',
            color: '#fff',
          },
        }}
      />
      
      {/* Header */}
      <header className="flex items-center justify-between mb-8" data-testid="portal-header">
        {/* Exit Button */}
        <Button
          variant="outline"
          onClick={handleExit}
          className="exit-btn border-stark-red text-stark-red hover:bg-stark-red/10 rounded-none"
          data-testid="exit-button"
        >
          <LogOut className="w-4 h-4 mr-2" />
          EXIT
        </Button>

        {/* Title */}
        <div className="flex items-center gap-3" data-testid="portal-title">
          <Heart 
            className="w-8 h-8 text-stark-red fill-stark-red animate-heartbeat" 
            data-testid="pulsing-heart"
          />
          <div className="flex flex-col items-center">
            <h1 className="text-2xl md:text-3xl font-bold tracking-widest uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
              <span className="text-white">CARDIAC</span>{" "}
              <span className="text-stark-red">SOLUTIONS</span>
            </h1>
            <span className="text-xs tracking-[0.2em] uppercase text-stark-cyan" style={{ fontFamily: "'Share Tech Mono', monospace" }}>
              CUSTOMER PORTAL
            </span>
          </div>
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="save-btn bg-[#FF2A2A] hover:bg-[#E60000] text-white rounded-none px-6 font-mono uppercase tracking-wider hover:shadow-[0_0_15px_rgba(255,42,42,0.5)] transition-all duration-150"
          data-testid="save-button"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "SAVING..." : "SAVE"}
        </Button>
      </header>

      {/* Customer Information Section */}
      <section className="hud-panel p-6 mb-8" data-testid="customer-info-section">
        <h2 className="font-heading text-xl font-semibold text-stark-cyan tracking-wide uppercase mb-6">
          Customer Information
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Site Name */}
          <div className="lg:col-span-2">
            <label className="stark-label block mb-2">Site Name</label>
            <Input
              value={formData.site_name}
              onChange={(e) => handleInputChange("site_name", e.target.value)}
              placeholder="Enter site name"
              className="stark-input w-full"
              data-testid="input-site-name"
            />
          </div>

          {/* Unit Count */}
          <div>
            <label className="stark-label block mb-2">Unit Count</label>
            <Input
              type="number"
              value={aedItems.length}
              readOnly
              className="stark-input w-full bg-stark-muted cursor-not-allowed"
              data-testid="input-unit-count"
            />
          </div>

          {/* Empty cell for alignment */}
          <div className="hidden lg:block"></div>

          {/* Address */}
          <div className="lg:col-span-2">
            <label className="stark-label block mb-2">Address</label>
            <Input
              value={formData.address}
              onChange={(e) => handleInputChange("address", e.target.value)}
              placeholder="Street address"
              className="stark-input w-full"
              data-testid="input-address"
            />
          </div>

          {/* City */}
          <div>
            <label className="stark-label block mb-2">City</label>
            <Input
              value={formData.city}
              onChange={(e) => handleInputChange("city", e.target.value)}
              placeholder="City"
              className="stark-input w-full"
              data-testid="input-city"
            />
          </div>

          {/* State */}
          <div>
            <label className="stark-label block mb-2">State</label>
            <Input
              value={formData.state}
              onChange={(e) => handleInputChange("state", e.target.value)}
              placeholder="State"
              className="stark-input w-full"
              data-testid="input-state"
            />
          </div>

          {/* Zip */}
          <div>
            <label className="stark-label block mb-2">Zip Code</label>
            <Input
              value={formData.zip_code}
              onChange={(e) => handleInputChange("zip_code", e.target.value)}
              placeholder="Zip"
              className="stark-input w-full"
              data-testid="input-zip"
            />
          </div>

          {/* Empty cells for alignment */}
          <div className="hidden lg:block lg:col-span-3"></div>

          {/* Contact Section */}
          <div>
            <label className="stark-label block mb-2">First Name</label>
            <Input
              value={formData.first_name}
              onChange={(e) => handleInputChange("first_name", e.target.value)}
              placeholder="First name"
              className="stark-input w-full"
              data-testid="input-first-name"
            />
          </div>

          <div>
            <label className="stark-label block mb-2">Last Name</label>
            <Input
              value={formData.last_name}
              onChange={(e) => handleInputChange("last_name", e.target.value)}
              placeholder="Last name"
              className="stark-input w-full"
              data-testid="input-last-name"
            />
          </div>

          <div>
            <label className="stark-label block mb-2">Email Address</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="email@example.com"
              className="stark-input w-full"
              data-testid="input-email"
            />
          </div>

          <div>
            <label className="stark-label block mb-2">Phone Number</label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              placeholder="(555) 555-5555"
              className="stark-input w-full"
              data-testid="input-phone"
            />
          </div>
        </div>
      </section>

      {/* AED Line Items Section */}
      <section className="hud-panel p-6" data-testid="aed-items-section">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl font-semibold text-stark-cyan tracking-wide uppercase">
            AED Units
          </h2>
          <Button
            variant="outline"
            onClick={addAEDRow}
            className="add-aed-btn border-stark-cyan text-stark-cyan hover:bg-stark-cyan/10 rounded-none"
            data-testid="add-aed-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            ADD AED
          </Button>
        </div>

        <ScrollArea className="w-full">
          <div className="overflow-x-auto">
            <table className="aed-table" data-testid="aed-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Unit #</th>
                  <th>AED Serial Number</th>
                  <th>AED Sentinel ID</th>
                  <th>Lot Number</th>
                  <th>Exp Date</th>
                  <th>Install Date</th>
                  <th>Consumable Make</th>
                  <th>Model</th>
                  <th>Case/Cabinet</th>
                  <th>Location</th>
                  <th>911 District</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {aedItems.map((item, index) => (
                  <tr key={index} data-testid={`aed-row-${index}`}>
                    <td className="text-stark-cyan font-mono text-sm">{index + 1}</td>
                    <td>
                      <Input
                        value={item.unit_number}
                        onChange={(e) => handleAEDChange(index, "unit_number", e.target.value)}
                        placeholder="-"
                        className="stark-input w-16 text-sm"
                        data-testid={`aed-unit-number-${index}`}
                      />
                    </td>
                    <td>
                      <Input
                        value={item.aed_serial_number}
                        onChange={(e) => handleAEDChange(index, "aed_serial_number", e.target.value)}
                        placeholder="-"
                        className="stark-input w-28 text-sm"
                        data-testid={`aed-serial-${index}`}
                      />
                    </td>
                    <td>
                      <Input
                        value={item.aed_sentinel_id}
                        onChange={(e) => handleAEDChange(index, "aed_sentinel_id", e.target.value)}
                        placeholder="-"
                        className="stark-input w-28 text-sm"
                        data-testid={`aed-sentinel-${index}`}
                      />
                    </td>
                    <td>
                      <Input
                        value={item.lot_number}
                        onChange={(e) => handleAEDChange(index, "lot_number", e.target.value)}
                        placeholder="-"
                        className="stark-input w-24 text-sm"
                        data-testid={`aed-lot-${index}`}
                      />
                    </td>
                    <td>
                      <Input
                        type="date"
                        value={item.expiration_date}
                        onChange={(e) => handleAEDChange(index, "expiration_date", e.target.value)}
                        className="stark-input w-32 text-sm"
                        data-testid={`aed-exp-date-${index}`}
                      />
                    </td>
                    <td>
                      <Input
                        type="date"
                        value={item.install_date}
                        onChange={(e) => handleAEDChange(index, "install_date", e.target.value)}
                        className="stark-input w-32 text-sm"
                        data-testid={`aed-install-date-${index}`}
                      />
                    </td>
                    <td>
                      <Input
                        value={item.consumable_make}
                        onChange={(e) => handleAEDChange(index, "consumable_make", e.target.value)}
                        placeholder="-"
                        className="stark-input w-28 text-sm"
                        data-testid={`aed-make-${index}`}
                      />
                    </td>
                    <td>
                      <Input
                        value={item.model}
                        onChange={(e) => handleAEDChange(index, "model", e.target.value)}
                        placeholder="-"
                        className="stark-input w-24 text-sm"
                        data-testid={`aed-model-${index}`}
                      />
                    </td>
                    <td>
                      <Input
                        value={item.case_or_cabinet}
                        onChange={(e) => handleAEDChange(index, "case_or_cabinet", e.target.value)}
                        placeholder="-"
                        className="stark-input w-24 text-sm"
                        data-testid={`aed-case-${index}`}
                      />
                    </td>
                    <td>
                      <Input
                        value={item.location_in_facility}
                        onChange={(e) => handleAEDChange(index, "location_in_facility", e.target.value)}
                        placeholder="-"
                        className="stark-input w-32 text-sm"
                        data-testid={`aed-location-${index}`}
                      />
                    </td>
                    <td className="text-center">
                      <Checkbox
                        checked={item.is_911_district}
                        onCheckedChange={(checked) => handleAEDChange(index, "is_911_district", checked)}
                        className="border-stark-input-border data-[state=checked]:bg-stark-cyan data-[state=checked]:border-stark-cyan"
                        data-testid={`aed-911-${index}`}
                      />
                    </td>
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAEDRow(index)}
                        disabled={aedItems.length === 1}
                        className="text-stark-red hover:bg-stark-red/10 p-2"
                        data-testid={`aed-remove-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ScrollArea>
      </section>
    </div>
  );
};

export default CustomerPortal;
